import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { each, camelCase, map, uniq, cloneDeep, find } from 'lodash';
import { Dropdown, MenuItem } from 'react-bootstrap';
import accounting from 'accounting';
import pluralize from 'pluralize';

import Pie from '../graphs/pie';
import DropdownRange from '../../../../components/dropdown-range';

import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
// Import Selectors
import { getOpportunities } from '../../AnalysisReducer';
import styles from '../graphs/opportunity-graphs.css';

function getPursuitIndex(buckets = []) {
  const arr = uniq(map(buckets, item => item.pursuit_length));
  return arr.sort((a, b) => a - b);
}
function getDefautlIndexFromTotal(total) {
  const lowerIndex = Math.floor((total / 3) - 1);
  const upperIndex = Math.floor((2 * (total / 3)) - 1);
  return [lowerIndex, upperIndex];
}
function getBreakDownBuckets(buckets, pursuitIndex, minIndex, maxIndex) {
  const lowerPursuit = pursuitIndex[minIndex];
  const upperPursuit = pursuitIndex[maxIndex];

  const oneStat = {
    total: 0,
    amount: 0,
    min: null,
    max: 0,
    stats: {
      won: { total: 0, amount: 0, opportunityIds: [] },
      lost: { total: 0, amount: 0, opportunityIds: [] },
      noDecision: { total: 0, amount: 0, opportunityIds: [] },
    },
  };

  const results = {
    long: cloneDeep(oneStat),
    medium: cloneDeep(oneStat),
    short: cloneDeep(oneStat),
    total: cloneDeep(oneStat),
  };
  each(buckets, (item) => {
    let bucketType = 'medium';
    if (item.pursuit_length <= lowerPursuit) {
      bucketType = 'short';
    } else if (item.pursuit_length >= upperPursuit) {
      bucketType = 'long';
    }
    each([bucketType, 'total'], (type) => {
      const bucket = results[type];
      const stage = camelCase(item.stage);
      if (bucket.stats[stage]) {
        bucket.amount += item.amount;
        bucket.max = Math.max(bucket.max, item.pursuit_length);
        if (bucket.min === null) {
          bucket.min = item.pursuit_length;
        } else {
          bucket.min = Math.min(bucket.min, item.pursuit_length);
        }
        bucket.total += 1;
        bucket.stats[stage].total += 1;
        bucket.stats[stage].amount += item.amount;
        bucket.stats[stage].opportunityIds.push(item.opportunity_id);
      }
    });
  });
  return results;
}

const USER_PREFERENCE_REPORT_TYPE = 'pursuit-outcome-graph-report-type';

class PursuitLengthOutcomeGraphs extends Component {
  constructor(props) {
    super(props);
    this.state = {
      reportType: 'amount', // count
      pursuitIndex: [],
      bounds: [0, 0],
    };
    this.onSliderChange = this.onSliderChange.bind(this);
    this.setReportType = this.setReportType.bind(this);
    this.decrease = this.decrease.bind(this);
    this.increase = this.increase.bind(this);
  }

  componentWillMount() {
    const { opportunities } = this.props;
    const { userPreferences } = this.context;
    const pursuitIndex = getPursuitIndex(opportunities);
    const total = pursuitIndex.length;
    const bounds = getDefautlIndexFromTotal(total);
    const reportType = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    this.setState({ pursuitIndex, bounds, reportType });
  }

  onSliderChange(bounds) {
    this.setState({ bounds });
    this.props.onBoundChange(bounds);
  }

  setReportType(reportType) {
    this.setState({ reportType });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, reportType);
  }

  getBounds() {
    const { pursuitIndex, bounds } = this.state;
    const total = pursuitIndex.length;
    const defaultIndex = getDefautlIndexFromTotal(total);
    const lowerIndex = bounds[0] !== null && bounds[0] < total ? bounds[0] : defaultIndex[0];
    const upperIndex = bounds[1] !== null && bounds[1] < total ? bounds[1] : defaultIndex[1];
    return [lowerIndex, upperIndex];
  }

  getBucketSubTitle(bucket) {
    const minUnit = bucket.min <= 30 ? 'day' : 'month';
    const maxUnit = bucket.max <= 30 ? 'day' : 'month';
    const minValue = bucket.min <= 30 ? bucket.min : bucket.min / 30;
    const maxValue = bucket.max <= 30 ? bucket.max : bucket.max / 30;

    let min = accounting.formatNumber(minValue, minUnit === 'day' ? 0 : 1);
    let max = accounting.formatNumber(maxValue, maxUnit === 'day' ? 0 : 1);
    min = min.replace('.0', '');
    max = max.replace('.0', '');

    const minUnitPlural = pluralize(minUnit, minValue);
    const maxUnitPlural = pluralize(maxUnit, maxValue);

    return `${min} ${minUnitPlural} to ${max} ${maxUnitPlural}`;
  }
  decrease() {
    const currentBounds = this.getBounds();
    const lowerBound = currentBounds[0];
    const upperBound = currentBounds[1];
    const newLowerBound = Math.max(0, lowerBound - 1);
    const bounds = [newLowerBound, upperBound];
    this.setState({ bounds });
  }
  increase() {
    const { pursuitIndex } = this.state;
    const currentBounds = this.getBounds();
    const lowerBound = currentBounds[0];
    const upperBound = currentBounds[1];
    const newUpperBound = Math.min(upperBound + 1, pursuitIndex.length - 1);
    const bounds = [lowerBound, newUpperBound];
    this.setState({ bounds });
  }
  render() {
    const { hasNoDecisionSetup, opportunities } = this.props;
    const { reportType, pursuitIndex } = this.state;
    const bounds = this.getBounds();
    const buckets = getBreakDownBuckets(opportunities, pursuitIndex, bounds[0], bounds[1]);

    const slider = (<DropdownRange
      values={pursuitIndex}
      bounds={bounds}
      isMoney={false}
      decrease={this.decrease}
      increase={this.increase}
      onSliderChange={this.onSliderChange}
      displayProps={{ suffix: ' days', suffixIsUnit: true }}
      modalProps={{
        title: 'Short, Medium, and Long Pursuit Boundaries',
        lowerLabel: 'Medium Length Start',
        upperLabel: 'Long Length Start',
        fieldSuffix: 'days',
      }}
    />);

    const reportTypes = [
      { value: 'amount', label: 'Amount' },
      { value: 'count', label: 'Count' },
    ];
    const activeReportType = find(reportTypes, { value: reportType }) || { label: 'Amount' };

    return (
      <div className={`${styles.opportunityGraphs} opportunity-graphs`}>
        <div className="animated fadeIn">
          <div>
            <h5 className="sub-header-with-controls">
              <Dropdown
                id="pursuit-length-report-type-dropdown"
              >
                <Dropdown.Toggle bsSize="small">
                  {activeReportType.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {reportTypes.map(s => (
                    <MenuItem
                      key={s.value}
                      active={s.value === reportType}
                      onSelect={() => { this.setReportType(s.value); }}
                    >
                      {s.label}
                    </MenuItem>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              &nbsp;
              <span>outcome rates in short, medium, and long pursuits with boundaries:</span>
              &nbsp;
              {buckets.total.total && slider}
            </h5>
            <div className="row">
              <div className="col-sm-4">
                <Pie
                  reportType={reportType}
                  bucket={buckets.short}
                  type="small"
                  hasNoDecisionSetup={hasNoDecisionSetup}
                  subTitle={this.getBucketSubTitle(buckets.short)}
                  title="SHORT PURSUITS"
                  onChartSegmentSelect={this.props.onChartSegmentSelect}
                />
              </div>
              <div className="col-sm-4">
                <Pie
                  reportType={reportType}
                  bucket={buckets.medium}
                  type="medium"
                  hasNoDecisionSetup={hasNoDecisionSetup}
                  subTitle={this.getBucketSubTitle(buckets.medium)}
                  title="MEDIUM PURSUITS"
                  onChartSegmentSelect={this.props.onChartSegmentSelect}
                />
              </div>
              <div className="col-sm-4">
                <Pie
                  reportType={reportType}
                  bucket={buckets.long}
                  type="large"
                  hasNoDecisionSetup={hasNoDecisionSetup}
                  subTitle={this.getBucketSubTitle(buckets.long)}
                  title="LONG PURSUITS"
                  onChartSegmentSelect={this.props.onChartSegmentSelect}
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    );
  }
}

PursuitLengthOutcomeGraphs.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    opportunity_id: PropTypes.string.isRequired,
  })).isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  onBoundChange: PropTypes.func,
  onChartSegmentSelect: PropTypes.func,
};

PursuitLengthOutcomeGraphs.defaultProps = {
  onBoundChange: () => {},
  onChartSegmentSelect: () => {},
};

PursuitLengthOutcomeGraphs.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    opportunities: getOpportunities(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
  };
}

export default connect(mapStateToProps)(PursuitLengthOutcomeGraphs);
